import { useNavigate } from "react-router-dom";
import MenuTop from "../../../../components/menu/top/menu-top";
import './style.css';
import { useDeferredValue, useEffect, useMemo, useState } from "react";
import { fetchUsersOfCompanyService, fetchUsersService } from "../../../../features/users/service/users-service";
import Loader from "../../../../components/loader/loader";
import MenuContext, { DISPLAY_TYPE } from "../../../../components/menu/context/menu-context";
import { User } from "../../../../shared/interfaces/user";
import { Box, List, ListItem, ListItemText, Paper, Typography } from "@mui/material";
import Input from "../../../../components/inputs/common/input";

type SortField = 'name' | 'email' | 'role';
type SortDirection = 'asc' | 'desc';

const sortableColumns: Array<{ key: SortField; label: string; flex: number }> = [
    { key: 'name', label: 'Nome', flex: 1.2 },
    { key: 'email', label: 'E-mail', flex: 1.6 },
    { key: 'role', label: 'Acesso', flex: 1 },
];

export function ListUsers() {
    const navigate = useNavigate();
    const [ isLoading, setIsLoading ] = useState(false);
    const [users, setUsers] = useState<User[]>([]);
    const [sortField, setSortField] = useState<SortField>('name');
    const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
    const [filterValue, setFilterValue] = useState('');
    const deferredFilterValue = useDeferredValue(filterValue);

    const getSortValue = (user: User, field: SortField) => {
        switch (field) {
            case 'email':
                return user.email || '';
            case 'role':
                return user.role?.name || '';
            case 'name':
            default:
                return user.name || '';
        }
    };

    const handleSort = (field: SortField) => {
        if (field === sortField) {
            setSortDirection((prevDirection) => prevDirection === 'asc' ? 'desc' : 'asc');
            return;
        }

        setSortField(field);
        setSortDirection('asc');
    };

    const normalizedFilterValue = deferredFilterValue.trim().toLowerCase();

    const visibleUsers = useMemo(() => {
        const filteredUsers = normalizedFilterValue
            ? users.filter((user) => {
                const searchableFields = [
                    user.name || '',
                    user.email || '',
                    user.role?.name || '',
                ];

                return searchableFields.some((field) =>
                    field.toLowerCase().includes(normalizedFilterValue)
                );
            })
            : users;

        return [...filteredUsers].sort((currentUser, nextUser) => {
            const currentValue = getSortValue(currentUser, sortField);
            const nextValue = getSortValue(nextUser, sortField);
            const comparison = currentValue.localeCompare(nextValue, 'pt-BR', {
                sensitivity: 'base',
                numeric: true,
            });

            return sortDirection === 'asc' ? comparison : comparison * -1;
        });
    }, [normalizedFilterValue, sortDirection, sortField, users]);

    useEffect(() => {
        const userLogged = JSON.parse(localStorage.getItem('user') || '{}') as User;

        const fetchUsers = async () => {
            try {
                setIsLoading(true);

                if(userLogged.name.toLowerCase().includes('cauane')) {
                    const users = await fetchUsersService();
                    setUsers(users);
                    return;
                }

                const users = await fetchUsersOfCompanyService(userLogged.company.id as string);

                setUsers(users);
            } catch (error) {
                setIsLoading(false);
            } finally {
                setIsLoading(false);
            }
        };

        fetchUsers();
    }, []);

    return (
        <div className="container-users">
            <MenuTop />
            {isLoading && <Loader />}
            <div className='container-users__content'>
                <div className="container-users__content__title">
                    <h1 className="container-users__content__title-text">Listagem de usuários</h1>
                    <MenuContext 
                        display={DISPLAY_TYPE.INLINE} 
                        clickedAdd={() => navigate('/usuario')}
                    />
                </div>
                <div className="container-users__content__filter">
                    <Input
                        placeholder="Buscar usuários"
                        value={filterValue}
                        onChange={({ target: { value } }: any) => setFilterValue(value)}
                        width="100%"
                    />
                </div>
                
                {visibleUsers.length > 0 ? (
                    <Paper
                        elevation={0}
                        sx={{
                            borderRadius: 2,
                            overflowX: 'auto',
                            border: '1px solid #f0f0f0',
                            width: '100%',
                            margin: '0 auto'
                        }}
                    >
                        <Box sx={{ minWidth: 840 }}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    padding: '12px 20px',
                                    backgroundColor: '#fafafa',
                                    borderBottom: '1px solid #f0f0f0'
                                }}
                            >
                                {sortableColumns.map((column) => {
                                    const isActiveColumn = sortField === column.key;
                                    const directionIndicator = !isActiveColumn
                                        ? '↕'
                                        : sortDirection === 'asc'
                                            ? '↑'
                                            : '↓';

                                    return (
                                        <Box
                                            component="button"
                                            key={column.key}
                                            onClick={() => handleSort(column.key)}
                                            type="button"
                                            sx={{
                                                flex: column.flex,
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 0.75,
                                                border: 0,
                                                background: 'transparent',
                                                padding: 0,
                                                cursor: 'pointer',
                                                textAlign: 'left',
                                                color: isActiveColumn ? '#4db0b1' : '#4a4a4a',
                                                fontSize: '0.875rem',
                                                fontWeight: 600
                                            }}
                                        >
                                            <Typography
                                                component="span"
                                                variant="subtitle2"
                                                sx={{
                                                    fontWeight: 600,
                                                    color: 'inherit'
                                                }}
                                            >
                                                {column.label}
                                            </Typography>
                                            <Typography
                                                component="span"
                                                variant="caption"
                                                sx={{
                                                    color: isActiveColumn ? '#4db0b1' : '#9e9e9e',
                                                    fontWeight: 700
                                                }}
                                            >
                                                {directionIndicator}
                                            </Typography>
                                        </Box>
                                    );
                                })}
                            </Box>

                            <List disablePadding>
                                {visibleUsers.map((user, index) => (
                                    <ListItem
                                        key={user.id || index}
                                        onClick={() => navigate(`/usuario/${user.id}`)}
                                        sx={{
                                            display: 'flex',
                                            padding: '12px 20px',
                                            borderBottom: index < visibleUsers.length - 1 ? '1px solid #f0f0f0' : 'none',
                                            transition: 'background-color 0.2s',
                                            '&:hover': {
                                                backgroundColor: 'rgba(0, 0, 0, 0.03)',
                                                cursor: 'pointer',
                                            }
                                        }}
                                    >
                                        <ListItemText
                                            primary={<Typography variant="body2" noWrap>{user.name || '-'}</Typography>}
                                            sx={{ flex: sortableColumns[0].flex, margin: 0 }}
                                        />
                                        <ListItemText
                                            primary={<Typography variant="body2" noWrap>{user.email || '-'}</Typography>}
                                            sx={{ flex: sortableColumns[1].flex, margin: 0 }}
                                        />
                                        <ListItemText
                                            primary={<Typography variant="body2" noWrap>{user.role?.name || '-'}</Typography>}
                                            sx={{ flex: sortableColumns[2].flex, margin: 0 }}
                                        />
                                    </ListItem>
                                ))}
                            </List>
                        </Box>
                    </Paper>
                ) : (
                    !isLoading && (
                        <p className="empty-message">
                            {users.length > 0
                                ? 'Nenhum usuário encontrado para esse filtro.'
                                : 'Nenhum usuário encontrado.'}
                        </p>
                    )
                )}
            </div>
        </div>
    )
}
